// src/pages/RientroMaterialiPage.js
// Acquisti → Rientro Materiali. NON è un magazzino (nessuna giacenza/lotto/ubicazione): serve
// esclusivamente a registrare che del materiale acquistato per una commessa è rientrato dal
// cantiere, così il Motore Economico può scalarne il costo — ma SOLO quando il valore economico
// è realmente noto (dal Listino Materiali, o da un movimento economico "Materiali" già inserito
// per quella commessa). Nessun prezzo viene mai stimato o inventato.
import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, onSnapshot, doc, addDoc, updateDoc, deleteDoc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { formatValutaItaliana } from "../utils/clienteHelpers";
import { Plus, X, Trash2, RotateCcw, Paperclip, CheckCircle2, HelpCircle, FileText as FileIcon } from "lucide-react";
import { chiaveBozza, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza from "../components/AvvisoBozza";
import { UM_OPTIONS } from "./ListinoMaterialiPage";
import { DEFAULT_TIPO_RESO, DEFAULT_AUTOMEZZO_SUGGERITO, TEMPISTICHE, CONDIZIONI_RITIRO } from "../utils/rientroMaterialiConstants";
import { toLocalDateStr } from "../utils/dateUtils";
import { numIt } from "../utils/economiaCommessa";

const inp = {width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"};
const inpDisabled = {...inp,background:"#f9fafb",color:"#6b7280"};
const lbl = {fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4};
const norm = s => (s||"").trim().toLowerCase();

function FormRientro({ orgId, currentUser, onClose }) {
  const { addToast } = useToast();
  const [commesse, setCommesse] = useState([]);
  const [searchCommessa, setSearchCommessa] = useState("");
  const [commessaSel, setCommessaSel] = useState(null);
  const [listino, setListino] = useState([]);
  const [movimentiMateriali, setMovimentiMateriali] = useState([]);
  const [richiesteCommessa, setRichiesteCommessa] = useState([]);
  const [ordiniCommessa, setOrdiniCommessa] = useState([]);
  const [rientriCommessa, setRientriCommessa] = useState([]);
  const [saving, setSaving] = useState(false);
  const [allegati, setAllegati] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tipiReso, setTipiReso] = useState(DEFAULT_TIPO_RESO);
  const [automezzi, setAutomezzi] = useState(DEFAULT_AUTOMEZZO_SUGGERITO);

  const [form, setForm] = useState({
    descrizione:"", quantita:"", unitaMisura:"pz", note:"",
    dataRientro: toLocalDateStr(new Date()),
    tipoReso:"", automezzoSuggerito:"", tempistiche:"Media",
    referenteCantiere:"", indicazioniAccesso:"", indirizzoRitiro:"",
    condizioniRitiro: [],
  });
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js)
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "rientro", orgId, null),
    valore: { form, allegati, commessa: commessaSel },
    info: { data: form.dataRientro, titolo: `Rientro materiali${commessaSel?.name ? ` — ${commessaSel.name}` : ""}`, vai: { page: "rientro-materiali" } },
    applica: d => {
      if (d.form) setForm(f => ({ ...f, ...d.form }));
      if (Array.isArray(d.allegati)) setAllegati(d.allegati);
      if (d.commessa !== undefined) setCommessaSel(d.commessa);
    },
  });
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleCondizione = (c) => setForm(f=>({...f, condizioniRitiro: f.condizioniRitiro.includes(c) ? f.condizioniRitiro.filter(x=>x!==c) : [...f.condizioniRitiro, c] }));

  useEffect(()=>{
    if(!orgId) return;
    onSnapshot(query(collection(db,"projects"),where("organizationId","==",orgId),where("statoCommessa","==","attiva")),
      snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived&&!p.isParent)));
    onSnapshot(query(collection(db,"listinoMateriali"),where("organizationId","==",orgId)),
      snap=>setListino(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return onSnapshot(doc(db,"configRientroMateriali",orgId), snap=>{
      if(snap.exists()){
        const d = snap.data();
        setTipiReso(d.tipiReso?.length ? d.tipiReso : DEFAULT_TIPO_RESO);
        setAutomezzi(d.automezzi?.length ? d.automezzi : DEFAULT_AUTOMEZZO_SUGGERITO);
      }
    });
  },[orgId]);

  useEffect(()=>{
    if(!commessaSel){ setMovimentiMateriali([]); setRichiesteCommessa([]); setRientriCommessa([]); setOrdiniCommessa([]); return; }
    const u1 = onSnapshot(query(collection(db,"movimentiEconomici"),where("projectId","==",commessaSel.id),where("categoriaId","==","materiali"),where("organizationId","==",orgId)),
      snap=>setMovimentiMateriali(snap.docs.map(d=>({id:d.id,...d.data()}))));
    const u2 = onSnapshot(query(collection(db,"richiesteMateriali"),where("projectId","==",commessaSel.id),where("organizationId","==",orgId)),
      snap=>setRichiesteCommessa(snap.docs.map(d=>({id:d.id,...d.data()}))));
    const u3 = onSnapshot(query(collection(db,"rientriMateriali"),where("projectId","==",commessaSel.id),where("organizationId","==",orgId)),
      snap=>setRientriCommessa(snap.docs.map(d=>({id:d.id,...d.data()}))));
    // Materiale acquistato tramite il ciclo NF → ODA → Fornitori — prima invisibile qui:
    // il controllo "non rientrare più di quanto acquistato" non vedeva affatto gli acquisti
    // esterni veri, solo le richieste al magazzino interno. Contano solo gli ordini avviati
    // per davvero (da "Inviato" in poi), stessa soglia già usata nel Controllo Economico.
    const u4 = onSnapshot(query(collection(db,"ordiniAcquisto"),where("commessaId","==",commessaSel.id),where("organizationId","==",orgId)),
      snap=>setOrdiniCommessa(snap.docs.map(d=>({id:d.id,...d.data()})).filter(o=>!o.eliminato && o.stato && o.stato!=="Bozza" && o.stato!=="Da approvare" && o.stato!=="Approvato" && o.stato!=="Annullato")));
    return ()=>{u1();u2();u3();u4();};
  },[commessaSel]);

  const commesseFiltrate = commesse.filter(c=>
    !searchCommessa || c.name?.toLowerCase().includes(searchCommessa.toLowerCase()) || (c.codiceCommessa||"").toLowerCase().includes(searchCommessa.toLowerCase())
  ).sort((a,b)=>a.name?.localeCompare(b.name||"","it"));

  // Materiali già acquistati/richiesti per QUESTA commessa (Movimenti Economici "Materiali" +
  // Richieste Magazzino), con quantità residua = registrata - già rientrata in precedenza.
  // Usato SOLO per la verifica quantità (punto 4) — non per il valore economico.
  const candidatiCommessa = useMemo(()=>{
    const mappa = {};
    movimentiMateriali.forEach(m=>{
      // Un movimento generato da un rientro precedente (origineRientroId presente) va escluso
      // qui: la sua quantità negativa è già considerata sotto, tramite rientriCommessa. Senza
      // questa esclusione ogni rientro veniva sottratto due volte, facendo apparire "disponibile"
      // sempre più basso del vero a ogni rientro successivo sullo stesso materiale.
      if (m.origineRientroId) return;
      const desc = (m.dati?.descrizione||"").trim(); if(!desc) return;
      const key = norm(desc);
      if(!mappa[key]) mappa[key] = { descrizione:desc, unitaMisura:"", registrato:0, giaRientrato:0, fonte:"movimento" };
      mappa[key].registrato += parseFloat(m.dati?.quantita)||0;
    });
    richiesteCommessa.forEach(r=>{
      if(r.stato==="annullata") return;
      (r.righe||[]).forEach(riga=>{
        const desc = (riga.descrizione||"").trim(); if(!desc) return;
        const key = norm(desc);
        if(!mappa[key]) mappa[key] = { descrizione:desc, unitaMisura:riga.unitaMisura||"", registrato:0, giaRientrato:0, fonte:"richiesta" };
        mappa[key].registrato += parseFloat(riga.quantita)||0;
        if(!mappa[key].unitaMisura) mappa[key].unitaMisura = riga.unitaMisura||"";
      });
    });
    ordiniCommessa.forEach(o=>{
      (o.righe||[]).forEach(riga=>{
        const desc = (riga.descrizione||"").trim(); if(!desc) return;
        const key = norm(desc);
        if(!mappa[key]) mappa[key] = { descrizione:desc, unitaMisura:riga.unitaMisura||"", registrato:0, giaRientrato:0, fonte:"oda" };
        mappa[key].registrato += parseFloat(riga.quantita)||0;
        if(!mappa[key].unitaMisura) mappa[key].unitaMisura = riga.unitaMisura||"";
      });
    });
    rientriCommessa.forEach(rm=>{
      const key = norm(rm.descrizione); if(!key||!mappa[key]) return;
      mappa[key].giaRientrato += parseFloat(rm.quantita)||0;
    });
    return Object.values(mappa).map(v=>({...v, disponibile: v.registrato - v.giaRientrato}));
  },[movimentiMateriali, richiesteCommessa, rientriCommessa, ordiniCommessa]);

  // Suggerimenti mostrati mentre si scrive: materiali già visti per questa commessa + voci del Listino
  const suggerimenti = useMemo(()=>{
    if(!form.descrizione.trim()) return [];
    const q = norm(form.descrizione);
    const daCommessa = candidatiCommessa.filter(c=>norm(c.descrizione).includes(q)).map(c=>({...c,tipo:"commessa"}));
    const daListino = listino.filter(v=>norm(v.descrizione).includes(q) || norm(v.codiceArticolo).includes(q)).map(v=>({descrizione:v.descrizione, unitaMisura:v.unitaMisura, prezzoUnitario:v.prezzoUnitario, codiceArticolo:v.codiceArticolo, tipo:"listino"}));
    return [...daCommessa, ...daListino].slice(0,6);
  },[form.descrizione, candidatiCommessa, listino]);

  // Anteprima di cosa succederà al salvataggio — trasparenza totale per l'utente
  const anteprima = useMemo(()=>{
    if(!form.descrizione.trim()) return null;
    const key = norm(form.descrizione);
    const voceListino = listino.find(v=>norm(v.descrizione)===key);
    let prezzo=null, fonte=null;
    if(voceListino){ prezzo=voceListino.prezzoUnitario; fonte="listino"; }
    else {
      const match = movimentiMateriali.filter(m=>norm(m.dati?.descrizione)===key && numIt(m.dati?.prezzoUnitario)>0)
        .sort((a,b)=>(b.creadaAt?.seconds||0)-(a.creadaAt?.seconds||0));
      if(match[0]){ prezzo=numIt(match[0].dati.prezzoUnitario); fonte="movimento"; }
    }
    const candidato = candidatiCommessa.find(c=>norm(c.descrizione)===key);
    return { prezzo, fonte, candidato };
  },[form.descrizione, listino, movimentiMateriali, candidatiCommessa]);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const estensione = file.name.split(".").pop().toLowerCase();
      const nomeFile = `rientri-materiali/${orgId}/${commessaSel?.id||"na"}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, nomeFile);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const tipo = ["jpg","jpeg","png","webp","heic"].includes(estensione) ? "foto" : "pdf";
      setAllegati(a => [...a, { url, nome:file.name, tipo, path:nomeFile }]);
      addToast(`${tipo==="foto"?"Foto":"File"} caricato ✅`,"success");
    } catch(err){ addToast("Errore nel caricamento","error"); }
    setUploading(false);
  };
  const rimuoviAllegato = (i) => setAllegati(a=>a.filter((_,idx)=>idx!==i));

  const salva = async () => {
    if(!commessaSel){ addToast("Seleziona una commessa","error"); return; }
    const descrizioneFinale = form.descrizione.trim();
    if(!descrizioneFinale){ addToast("Indica il materiale rientrato","error"); return; }
    const quantitaNum = parseFloat(form.quantita.toString().replace(",","."));
    if(!quantitaNum || quantitaNum<=0){ addToast("Indica una quantità valida","error"); return; }
    if(!form.tipoReso){ addToast("Seleziona il tipo di reso","error"); return; }
    if(!form.automezzoSuggerito){ addToast("Seleziona l'automezzo suggerito","error"); return; }

    const key = norm(descrizioneFinale);
    const candidato = candidatiCommessa.find(c=>norm(c.descrizione)===key);
    if(candidato && quantitaNum > candidato.disponibile){
      addToast(`Quantità superiore a quella disponibile per questa commessa: risultano ${candidato.disponibile} ${candidato.unitaMisura||""} residui (già acquistati/richiesti meno quanto già rientrato). Correggi la quantità o verifica il materiale.`,"error");
      return;
    }

    setSaving(true);
    try {
      const voceListino = listino.find(v=>norm(v.descrizione)===key);
      let prezzo=null, fonte=null;
      if(voceListino){ prezzo=voceListino.prezzoUnitario; fonte="listino"; }
      else {
        const match = movimentiMateriali.filter(m=>norm(m.dati?.descrizione)===key && numIt(m.dati?.prezzoUnitario)>0)
          .sort((a,b)=>(b.creadaAt?.seconds||0)-(a.creadaAt?.seconds||0));
        if(match[0]){ prezzo=numIt(match[0].dati.prezzoUnitario); fonte="movimento"; }
      }

      const anno = new Date().getFullYear();
      const prog = await prossimoProgressivo(`${orgId}_rm_${anno}`, { orgId, anno });
      const numero = `RM-${anno}-${String(prog).padStart(4,"0")}`;

      const rientroRef = await addDoc(collection(db,"rientriMateriali"), {
        numero, organizationId:orgId,
        projectId:commessaSel.id, projectName:commessaSel.name, codiceCommessa:commessaSel.codiceCommessa||"",
        clienteNome: commessaSel.clienteNome||"",
        pmNome: commessaSel.responsabileNome||"",
        descrizione:descrizioneFinale, quantita:quantitaNum, unitaMisura:form.unitaMisura, note:form.note.trim(),
        dataRientro:form.dataRientro, allegati,
        tipoReso:form.tipoReso, automezzoSuggerito:form.automezzoSuggerito, tempistiche:form.tempistiche,
        referenteCantiere:form.referenteCantiere.trim(), indicazioniAccesso:form.indicazioniAccesso.trim(),
        indirizzoRitiro:form.indirizzoRitiro.trim(),
        condizioniRitiro:form.condizioniRitiro,
        compilatoreUid:currentUser.uid, compilatoreNome:currentUser.displayName||"",
        valoreNoto: !!prezzo, fonteValore:fonte, prezzoUnitarioApplicato:prezzo,
        registratoAlMomento: candidato?candidato.registrato:null,
        movimentoEconomicoId:null,
        createdAt: serverTimestamp(),
      });

      if(prezzo){
        const movRef = await addDoc(collection(db,"movimentiEconomici"),{
          projectId:commessaSel.id, clienteId:commessaSel.clienteId||"", organizationId:orgId,
          tipo:"costo", categoriaId:"materiali", categoriaLabel:"Materiali",
          dati:{ data:form.dataRientro, descrizione:`🔄 Rientro materiale: ${descrizioneFinale}`, fornitore:"Rientro da cantiere", quantita:-quantitaNum, prezzoUnitario:prezzo },
          importo: -(quantitaNum*prezzo),
          origineRientroId: rientroRef.id,
          creadaDa:currentUser.uid, creadaAt:serverTimestamp(),
        });
        await updateDoc(rientroRef,{ movimentoEconomicoId: movRef.id });
        addToast(`Rientro ${numero} registrato — Motore Economico aggiornato (-€ ${formatValutaItaliana(quantitaNum*prezzo)}) ✅`,"success");
      } else {
        addToast(`Rientro ${numero} registrato. Valore economico non disponibile per questo materiale: non è stato considerato nei calcoli della commessa.`,"info");
      }
      onClose();
    } catch(err){ addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:560,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
            <RotateCcw size={16} color="#7c3aed"/> Nuovo Rientro Materiali
          </h3>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={16}/></button>
        </div>
        {bozza.trovata && <AvvisoBozza testo="Hai un rientro materiali iniziato e non salvato"
          salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <label style={lbl}>Numero richiesta</label>
            <input style={inpDisabled} value="Generato automaticamente" disabled/>
          </div>
          <div>
            <label style={lbl}>Data</label>
            <input style={inpDisabled} value={new Date().toLocaleDateString("it-IT")} disabled/>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <label style={lbl}>Nome del richiedente</label>
            <input style={inpDisabled} value={currentUser?.displayName||""} disabled/>
          </div>
          <div>
            <label style={lbl}>PM automatico</label>
            <input style={inpDisabled} value={commessaSel?.responsabileNome || "—"} disabled/>
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <label style={lbl}>Commessa *</label>
            <span style={{fontSize:11,color:"#7c3aed",fontWeight:500}}>💜 Nexiora – il modulo si attiva inserendo il numero o il nome della commessa</span>
          </div>
          {commessaSel ? (
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:"#ede9fe",border:"2px solid #7c3aed",borderRadius:8}}>
              <span style={{flex:1,fontSize:13,fontWeight:700,color:"#7c3aed"}}>
                ✓ {commessaSel.codiceCommessa && `[${commessaSel.codiceCommessa}] `}{commessaSel.name}
                {commessaSel.clienteNome && <span style={{fontWeight:500,color:"#6d28d9"}}> · {commessaSel.clienteNome}</span>}
              </span>
              <button type="button" onClick={()=>{setCommessaSel(null);setSearchCommessa("");}} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:0}}><X size={14}/></button>
            </div>
          ) : (
            <div>
              <input style={inp} value={searchCommessa} onChange={e=>setSearchCommessa(e.target.value)} placeholder="🔍 Cerca per numero o nome commessa..."/>
              {searchCommessa && (
                <div style={{border:"1px solid #e5e7eb",borderRadius:8,maxHeight:160,overflowY:"auto",background:"#fff",marginTop:6}}>
                  {commesseFiltrate.length===0 ? (
                    <div style={{padding:"12px 14px",color:"#9ca3af",fontSize:13}}>Nessuna commessa trovata</div>
                  ) : commesseFiltrate.map(c=>(
                    <div key={c.id} onClick={()=>{setCommessaSel(c);setSearchCommessa("");}}
                      style={{padding:"9px 14px",cursor:"pointer",fontSize:13,borderBottom:"1px solid #f3f4f6",fontWeight:500}}>
                      {c.codiceCommessa && <span style={{color:"#7c3aed",fontFamily:"monospace",marginRight:6}}>[{c.codiceCommessa}]</span>}{c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <label style={lbl}>Tipo di reso *</label>
            <select style={inp} value={form.tipoReso} onChange={e=>upd("tipoReso",e.target.value)} disabled={!commessaSel}>
              <option value="">Seleziona</option>
              {tipiReso.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Automezzo suggerito *</label>
            <select style={inp} value={form.automezzoSuggerito} onChange={e=>upd("automezzoSuggerito",e.target.value)} disabled={!commessaSel}>
              <option value="">Seleziona</option>
              {automezzi.map(a=><option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <label style={lbl}>Tempistiche</label>
          <select style={inp} value={form.tempistiche} onChange={e=>upd("tempistiche",e.target.value)} disabled={!commessaSel}>
            {TEMPISTICHE.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div style={{marginBottom:4,position:"relative"}}>
          <label style={lbl}>Materiale rientrato *</label>
          <input style={inp} value={form.descrizione} onChange={e=>upd("descrizione",e.target.value)}
            placeholder="Es. Cavo FG16OR16 3G2.5mm²" disabled={!commessaSel}/>
          {suggerimenti.length>0 && (
            <div style={{border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",marginTop:4,boxShadow:"0 4px 12px rgba(0,0,0,0.08)"}}>
              {suggerimenti.map((s,i)=>(
                <div key={i} onClick={()=>{
                  upd("descrizione",s.descrizione);
                  if(s.unitaMisura) upd("unitaMisura",s.unitaMisura);
                }} style={{padding:"8px 12px",cursor:"pointer",fontSize:12,borderBottom:i<suggerimenti.length-1?"1px solid #f3f4f6":"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontWeight:600,color:"#111827"}}>{s.descrizione}{s.codiceArticolo && <span style={{fontWeight:400,color:"#9ca3af",fontFamily:"monospace",fontSize:10.5}}> · {s.codiceArticolo}</span>}</span>
                  {s.tipo==="commessa" ? (
                    <span style={{fontSize:10,color:"#0891b2",fontWeight:700}}>{s.disponibile} {s.unitaMisura||""} disp. in commessa</span>
                  ):(
                    <span style={{fontSize:10,color:"#059669",fontWeight:700}}>📋 Listino · € {formatValutaItaliana(s.prezzoUnitario)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Anteprima trasparente di cosa succederà */}
        {anteprima && commessaSel && (
          <div style={{marginBottom:14,marginTop:8,padding:"9px 12px",borderRadius:8,fontSize:11.5,
            background: anteprima.prezzo?"#f0fdf4":"#fffbeb", border:`1px solid ${anteprima.prezzo?"#bbf7d0":"#fde68a"}`,
            color: anteprima.prezzo?"#065f46":"#92400e", display:"flex",alignItems:"flex-start",gap:6}}>
            {anteprima.prezzo ? <CheckCircle2 size={14} style={{flexShrink:0,marginTop:1}}/> : <HelpCircle size={14} style={{flexShrink:0,marginTop:1}}/>}
            <span>
              {anteprima.prezzo
                ? <>Valore noto (fonte: {anteprima.fonte==="listino"?"Listino Materiali":"movimento economico già registrato"}, € {formatValutaItaliana(anteprima.prezzo)}/{form.unitaMisura}) — al salvataggio il Motore Economico della commessa verrà aggiornato automaticamente.</>
                : <>Valore economico non disponibile per questo materiale — il rientro verrà comunque registrato, ma <strong>non</strong> aggiornerà i calcoli economici della commessa.</>}
              {anteprima.candidato ? <> · {anteprima.candidato.disponibile} {anteprima.candidato.unitaMisura||""} disponibili per questa commessa.</>
                : <> · Nessun acquisto/richiesta trovato per questa commessa con questo materiale: la quantità non può essere verificata.</>}
            </span>
          </div>
        )}

        {anteprima && !anteprima.prezzo && (
          <div style={{marginTop:8,marginBottom:14,background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"8px 14px",fontSize:11,color:"#5b21b6"}}>
            💜 <strong>Nexiora:</strong> se in seguito aggiungi questo materiale al Listino Materiali (o registri un nuovo acquisto), il valore economico varrà solo per i <strong>prossimi</strong> rientri — questo rientro resta registrato ma non entrerà retroattivamente nei calcoli di Controllo Economico. Per farlo contare, va eliminato e ricreato dopo aver impostato il prezzo.
          </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <label style={lbl}>Quantità *</label>
            <input style={inp} type="text" inputMode="decimal" value={form.quantita} onChange={e=>upd("quantita",e.target.value)} placeholder="Es. 25" disabled={!commessaSel}/>
          </div>
          <div>
            <label style={lbl}>U.M.</label>
            <select style={inp} value={form.unitaMisura} onChange={e=>upd("unitaMisura",e.target.value)} disabled={!commessaSel}>
              {UM_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Data rientro</label>
            <input type="date" style={inp} value={form.dataRientro} onChange={e=>upd("dataRientro",e.target.value)} disabled={!commessaSel}/>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <label style={lbl}>Eventuale Referente di Cantiere</label>
            <input style={inp} value={form.referenteCantiere} onChange={e=>upd("referenteCantiere",e.target.value)} placeholder="Nome e recapito" disabled={!commessaSel}/>
          </div>
          <div>
            <label style={lbl}>Indirizzo di ritiro (se diverso dalla sede della commessa)</label>
            <input style={inp} value={form.indirizzoRitiro} onChange={e=>upd("indirizzoRitiro",e.target.value)} placeholder="Via, città..." disabled={!commessaSel}/>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <label style={lbl}>Eventuali indicazioni per l'accesso</label>
          <textarea style={{...inp,minHeight:38,resize:"vertical"}} value={form.indicazioniAccesso} onChange={e=>upd("indicazioniAccesso",e.target.value)} placeholder="Es. codice cancello, orari, referente in loco..." disabled={!commessaSel}/>
        </div>

        <div style={{marginBottom:14,padding:"10px 12px",border:"1px solid #e5e7eb",borderRadius:8}}>
          <label style={{...lbl,marginBottom:8}}>Condizioni per il ritiro</label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {CONDIZIONI_RITIRO.map(c=>(
              <label key={c} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#374151",cursor:commessaSel?"pointer":"default"}}>
                <input type="checkbox" checked={form.condizioniRitiro.includes(c)} onChange={()=>toggleCondizione(c)} disabled={!commessaSel}/>
                {c}
              </label>
            ))}
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>Note (opzionale)</label>
          <textarea style={{...inp,minHeight:56,resize:"vertical"}} value={form.note} onChange={e=>upd("note",e.target.value)} placeholder="Es. materiale in buono stato, riutilizzabile su altra commessa..." disabled={!commessaSel}/>
        </div>

        <div style={{marginBottom:16}}>
          <label style={lbl}>Allegati (opzionale — foto o PDF)</label>
          {allegati.map((a,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,fontSize:12,marginBottom:6}}>
              {a.tipo==="foto" ? <Paperclip size={12}/> : <FileIcon size={12}/>} {a.nome}
              <button type="button" onClick={()=>rimuoviAllegato(i)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={13}/></button>
            </div>
          ))}
          <input type="file" accept="image/*,.pdf" disabled={uploading||!commessaSel} onChange={e=>{ if(e.target.files[0]) uploadFile(e.target.files[0]); e.target.value=""; }} style={{fontSize:12}}/>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" disabled={saving} onClick={()=>bozza.salvaCon(salva)}>{saving?"Salvataggio...":"💾 Registra rientro"}</button>
        </div>
      </div>
    </div>
  );
}

export default function RientroMaterialiPage() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const canAdmin = isOrgAdmin || isPlatformOwner;

  const [rientri, setRientri] = useState([]);
  const [showForm, setShowForm] = useState(false);

  useEffect(()=>{
    if(!effectiveOrgId||!currentUser) return;
    return onSnapshot(query(collection(db,"rientriMateriali"),where("organizationId","==",effectiveOrgId),where("compilatoreUid","==",currentUser.uid)),
      snap=>setRientri(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))));
  },[effectiveOrgId,currentUser]);

  const elimina = async (r) => {
    if(!await confirm(`Eliminare il rientro ${r.numero}? ${r.movimentoEconomicoId?"Verrà rimosso anche il relativo movimento economico.":""}`,{type:"danger"})) return;
    if(r.movimentoEconomicoId){ try{ await deleteDoc(doc(db,"movimentiEconomici",r.movimentoEconomicoId)); }catch{} }
    await deleteDoc(doc(db,"rientriMateriali",r.id));
    addToast("Rientro eliminato","info");
  };

  return (
    <div style={{padding:24,maxWidth:960,flex:1,overflowY:"auto"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
        <h2 style={{fontSize:18,fontWeight:800,display:"flex",alignItems:"center",gap:8,margin:0}}>
          <RotateCcw size={18} color="#7c3aed"/> Rientro Materiali
        </h2>
        <button onClick={()=>setShowForm(true)} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:5}}>
          <Plus size={14}/> Nuovo Rientro Materiali
        </button>
      </div>
      <p style={{fontSize:12,color:"#9ca3af",marginBottom:16}}>
        Registra il rientro dal cantiere di materiale acquistato per una commessa, così il Motore Economico può scalarne il costo quando il valore è noto. Non è un magazzino: nessuna giacenza, nessun lotto. Qui vedi solo i rientri che hai registrato tu — la vista completa per tutta l'azienda è nel Registro Rientri Materiali (Gestione → Registri).
      </p>

      {rientri.length===0 ? (
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <div style={{fontSize:48,marginBottom:12,opacity:.15}}>🔄</div>
          <div style={{fontSize:14,fontWeight:600}}>Nessun rientro registrato da te</div>
        </div>
      ) : (
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
          <thead>
            <tr style={{borderBottom:"2px solid #e5e7eb"}}>
              {["N°","Data","Commessa","Materiale","Tipo di reso","Quantità","Valore economico","Registrato da",""].map(h=>(
                <th key={h} style={{textAlign:"left",padding:"7px 10px",fontSize:11,color:"#9ca3af",fontWeight:700}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rientri.map(r=>(
              <tr key={r.id} style={{borderBottom:"1px solid #f3f4f6"}}>
                <td style={{padding:"8px 10px",fontFamily:"monospace",fontWeight:800,color:"#7c3aed"}}>{r.numero}</td>
                <td style={{padding:"8px 10px",color:"#6b7280",whiteSpace:"nowrap"}}>{r.dataRientro?.split("-").reverse().join("/")}</td>
                <td style={{padding:"8px 10px",color:"#374151"}}>{r.codiceCommessa && `[${r.codiceCommessa}] `}{r.projectName}{r.clienteNome && <span style={{color:"#9ca3af"}}> · {r.clienteNome}</span>}</td>
                <td style={{padding:"8px 10px",fontWeight:600,color:"#111827"}}>{r.descrizione}</td>
                <td style={{padding:"8px 10px",color:"#6b7280",fontSize:11.5}}>{r.tipoReso||"—"}</td>
                <td style={{padding:"8px 10px"}}>{r.quantita} {r.unitaMisura}</td>
                <td style={{padding:"8px 10px"}}>
                  {r.valoreNoto ? (
                    <span style={{fontSize:11,fontWeight:700,color:"#059669"}}>✓ -€ {formatValutaItaliana(r.quantita*r.prezzoUnitarioApplicato)}</span>
                  ) : (
                    <span style={{fontSize:11,fontWeight:700,color:"#d97706"}}>⚠️ non disponibile</span>
                  )}
                </td>
                <td style={{padding:"8px 10px",color:"#6b7280"}}>{r.compilatoreNome}</td>
                <td style={{padding:"8px 10px"}}>
                  {(canAdmin || r.compilatoreUid===currentUser?.uid) && (
                    <button onClick={()=>elimina(r)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}><Trash2 size={14}/></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && <FormRientro orgId={effectiveOrgId} currentUser={currentUser} onClose={()=>setShowForm(false)}/>}
    </div>
  );
}
